import { Card, Typography, Input, Button, Form, DatePicker, Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "jspdf-autotable";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { changeLoader } from "../../../../../../../redux/reducers/loader";
import toast from "react-hot-toast";
import globalRequest from "../../../../../../../utils/global/globalRequest";
import { debounce } from "lodash";
import * as Apis from "../../../../../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { paginationLimit } from "../../../../../../../utils/helper/constants";
import dayjs from "dayjs";
import * as Validation from "../../../../../../../utils/helper/validation";
import { exportCSV, exportPDf, getTabFromOrderStatus, roundToDecimal } from "../../../../../../../utils/helper/utils";

const expandColumns = [
  {
    title: "Order Number",
    dataIndex: "OrderNumber",
    key: "OrderNumber",
    render: (text, record) => (
      <Link className="cursor-pointer" to={`/order-management/?order_number=${record?.OrderNumber}`}
        state={{
          orderNumber: record?.OrderNumber,
          activeTab: getTabFromOrderStatus(record?.Status)
        }}
        style={{ color: "#37846B", fontWeight: 500 }}
      >
        {text}
      </Link>
    ),
  },
  {
    title: "Qty",
    dataIndex: "Qty",
    key: "Qty",
  },
  {
    title: "Total Amount",
    dataIndex: "TotalAmount",
    key: "TotalAmount",
  },
];

const CustomerOrder = () => {
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const { Search } = Input;
  const dispatch = useDispatch();
  const { id } = useParams();

  const [dataSource, setDataSource] = useState([]);
  const [paginationModel] = useState(paginationLimit); //setPaginationModel
  const location = useLocation();
  const { search, start_date, end_date } = location.state?.inputData || {};
  const tabIndex = location.state?.tabIndex; // Get the tabIndex from location state
  const navigate = useNavigate();
  const [inputData, setInputData] = useState({
    search: search,
    start_date: start_date, // Set the start date
    end_date: end_date, // Set the end date
  });

  const handleDateChange = (dates, dateStrings) => {
    const [startdate, enddate] = dateStrings; // Get the date strings from the RangePicker

    // Update the filter with the selected start and end dates
    setInputData((prevState) => ({
      ...prevState,
      start_date: startdate, // Set the start date
      end_date: enddate, // Set the end date
    }));
  };

  const handleSearchDebounced = debounce((value) => {
    setInputData((prev) => ({ ...prev, search: value }));
  }, 300); // Debounce delay in milliseconds (adjust as needed)

  // Fetch orders dynamically for expanded row
  const fetchOrdersForItem = async (record) => {
    dispatch(changeLoader(true));
    try {
      let params = {
        ...inputData,
        page: paginationModel?.page,
        limit: paginationModel?.limit,
      };
      const response = await globalRequest(
        Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_CUSTOMER_ORDERS + "/" + id,
        "get",
        {},
        { params: params },
        true
      );

      if (response?.data?.status === "SUCCESS") {
        const orders = response?.data?.data?.data?.data?.map((order, index) => ({
          key: index,
          OrderNumber: order.orderNumber,
          Qty: order.totalQuantity,
          Status: order.status,
          TotalAmount: roundToDecimal(order.totalAmount, 2),
        }));
        setDataSource(orders);
      }
    } catch (e) {
      toast.error(e?.message);
    }
    dispatch(changeLoader(false));
  };
  const exportData = async (value) => {
    if (value) {
      //api call for get data
      dispatch(changeLoader(true));
      try {
        let params = {
          ...inputData,
          type: "export",
        };
        let response = await globalRequest(
          Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_CUSTOMER_ORDERS + "/" + id,
          "get",
          {},
          { params: params },
          true
        );
        let apiResponse = response.data;
        if (apiResponse?.status === "SUCCESS") {
          if (apiResponse?.data) {
            let exportData = [];
            for (let key in apiResponse?.data?.data?.data) {
              let item = apiResponse?.data?.data?.data[key];
              exportData.push({
                OrderNumber: item.orderNumber,
                Qty: item.totalQuantity,
                TotalAmount: roundToDecimal(item.totalAmount, 2),
              });
            }
            if (exportData.length > 0) {
              if (value === "csv") {
                exportCSV(exportData, "customerOrderReport.csv");
              }
              if (value === "pdf") {
                // Generate the table data
                const headers = ["order Number", "Qty", "TotalAmount"];
                const rows = exportData.map((item) => [item.OrderNumber, item.Qty, item.TotalAmount]);
                exportPDf(headers, rows, "customerOrderReport.pdf");
              }
            } else {
              // console.log("No data to export");
            }
          }
        } else {
          toast.error(apiResponse?.message);
        }
      } catch (e) {
        toast.error(e?.message);
      }
      dispatch(changeLoader(false));
    }
  };

  useEffect(() => {
    fetchOrdersForItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputData, paginationModel]);

  return (
    <>
      <Card>
        <div className="flex flex-wrap justify-between	mb-4 gap-5 ">
          <div className="">
            <Typography className="text-xl font-semibold flex gap-1" align={"left"}>
              <Link to="/report" className="!text-[#176B53]" state={{ 
                  inputData: inputData,
                  mainTabIndex: "1", 
                  subTabIndex: tabIndex 
                }}>
                Reports /{" "}
              </Link>
              <Typography
                onClick={() => navigate("/report", { state: { inputData: inputData,
                    mainTabIndex: "1",
                    subTabIndex: tabIndex 
                  } 
                })}
                className="!text-xl !text-[#176B53] cursor-pointer"
              >
                Sales Reports / Sales By Customer
              </Typography>
            </Typography>
          </div>
          <div className="flex flex-wrap gap-3">
            <Form form={form} layout="vertical">
              <Form.Item name="search">
                <div className="flex flex-wrap gap-3">
                  <RangePicker
                    className="cursor-pointer"
                    onChange={handleDateChange}
                    value={[
                      inputData?.start_date ? dayjs(inputData?.start_date, "YYYY-MM-DD") : null,
                      inputData?.end_date ? dayjs(inputData?.end_date, "YYYY-MM-DD") : null,
                    ]}
                    // defaultValue={ inputData.start_date && inputData.end_date ? [dayjs(inputData.start_date, 'YYYY-MM-DD'), dayjs(inputData.end_date, 'YYYY-MM-DD')] : null}
                    format="YYYY-MM-DD"
                  />
                  <Search
                    placeholder="Search"
                    style={{
                      width: window.innerWidth < 768 ? "100%" : 200,
                    }}
                    // value={inputData?.search}
                    className="cursor-pointer"
                    onChange={(e) => {
                      const value = Validation.strFormat(e.target.value, "trim");
                      handleSearchDebounced(value); // Debounced function
                    }}
                  />
                </div>
              </Form.Item>
            </Form>
            <div className="flex flex-wrap gap-3">
              <>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("csv");
                  }}
                >
                  Download in CSV
                </Button>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("pdf");
                  }}
                >
                  Download in PDF
                </Button>
              </>
            </div>
          </div>
        </div>
        <Table columns={expandColumns} dataSource={dataSource} pagination={true} className="multi-tables responsive" />
      </Card>
    </>
  );
};
export default CustomerOrder;
